import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

// The installation's public settings (support/contact addresses, legal document URLs, marketing
// link) from the anonymous GET /instance endpoint. Plain axios, like the signup pages' /Regions
// call: the shared api instance's org headers and 401/403 redirects don't belong on public pages.
//
// One request per page load: the promise is cached at module level and every mounted hook shares
// its result. A failed request is never cached, so a later mount or retry() refetches.
//
// While loading and on error, `info` is the all-null FALLBACK: nothing Axaris-specific renders
// before (or without) the server's answer. `error` is set only when the request failed, so callers
// that must fail closed (acceptance steps) can tell a failure from a load with no documents.

const FALLBACK = Object.freeze({
    supportEmail: null,
    contactEmail: null,
    legal: Object.freeze({
        termsOfUseUrl: null,
        privacyPolicyUrl: null,
        dataProcessingAgreementUrl: null,
        vendorInviteeTermsUrl: null,
    }),
    showMarketingSiteLink: false,
});

const INITIAL_STATE = { status: "idle", data: null, error: null };

let promise = null;
let state = INITIAL_STATE;
const listeners = new Set();

const setState = (next) => {
    state = next;
    listeners.forEach((listener) => listener(state));
};

const normalize = (d) => ({
    supportEmail: d?.supportEmail || null,
    contactEmail: d?.contactEmail || null,
    legal: {
        termsOfUseUrl: d?.legal?.termsOfUseUrl || null,
        privacyPolicyUrl: d?.legal?.privacyPolicyUrl || null,
        dataProcessingAgreementUrl: d?.legal?.dataProcessingAgreementUrl || null,
        vendorInviteeTermsUrl: d?.legal?.vendorInviteeTermsUrl || null,
    },
    showMarketingSiteLink: d?.showMarketingSiteLink === true,
});

const load = () => {
    if (promise) return promise;
    setState({ status: "loading", data: null, error: null });
    const request = axios.get(`${API_BASE_URL}/instance`)
        .then((res) => {
            if (promise !== request) return;
            // Anything but a JSON object (e.g. a proxy serving index.html for this path) is a
            // failure, not "nothing configured": acceptance steps must fail closed on it.
            const body = res?.data;
            if (body === null || typeof body !== "object" || Array.isArray(body)) {
                throw new Error("Unexpected instance settings response");
            }
            setState({ status: "success", data: normalize(res.data), error: null });
        })
        .catch((error) => {
            if (promise !== request) return;
            promise = null;
            setState({ status: "error", data: null, error: error || new Error("Request failed") });
        });
    promise = request;
    return promise;
};

// A failure has already cleared the cached promise, so this refetches; while a request is in
// flight or after a success it returns the cached one. Every mounted hook sees the outcome.
const retry = () => load();

// Tests only: forget the cached request and result. Unmount every hook first: listeners are
// dropped, so a still-mounted hook would stop receiving updates.
export const resetInstanceInfoCache = () => {
    promise = null;
    state = INITIAL_STATE;
    listeners.clear();
};

const useInstanceInfo = () => {
    const [current, setCurrent] = useState(state);

    useEffect(() => {
        listeners.add(setCurrent);
        // Pick up anything that settled between render and subscribe.
        setCurrent(state);
        // Only the first mount starts the request. After a failure, recovery is through retry()
        // alone, so a late-mounting consumer doesn't flip every hook from error back to loading.
        if (state.status === "idle") load();
        return () => { listeners.delete(setCurrent); };
    }, []);

    return {
        info: current.status === "success" ? current.data : FALLBACK,
        loading: current.status === "idle" || current.status === "loading",
        error: current.status === "error" ? current.error : null,
        retry,
    };
};

export default useInstanceInfo;
